import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { TableCell, Anchor, MediaPreview, ButtonLink } from '@anytag/shared-atoms';
import { useTranslateCountry } from '@anytag/shared-hooks';
import { formatPercent, formatIntNumber } from '@anytag/shared-utils';
import { AutoManagedCampaign } from '../types';

export const useMarketplaceAutoManagedCampaignTable = () => {
  const { t } = useTranslation();
  const { tc } = useTranslateCountry();

  const columns: ColumnDef<AutoManagedCampaign>[] = [
    {
      accessorKey: 'title',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Product Name'),
      cell: ({ row }) => (
        <div className="flex w-75 items-end">
          <Anchor target="_blank" href={row.original.landingPageUrl || ''}>
            <MediaPreview src={row.original.thumbnailUrl} className="size-20" />
          </Anchor>
          <div className="ml-2 w-full">
            <Anchor
              target="_blank"
              variant="blue"
              label={row.original.title}
              className="text-lg font-semibold"
              href={row.original.landingPageUrl || ''}
            />
            <ButtonLink
              variant="outline"
              title={t('Select Offer')}
              className="mt-[15px]"
              to="/marketplace/add/e-commerce/$autoMangedCampaignId"
              params={{ autoMangedCampaignId: row.original.id.toString() }}
            />
          </div>
        </div>
      )
    },
    {
      accessorKey: 'autoManagedCampaignType',
      header: t<string>('HeaderColumn.Marketplace'),
      cell: ({ row }) => <TableCell.Text value={row.original.autoManagedCampaignType} className="min-w-0" />
    },
    {
      accessorKey: 'country',
      header: t<string>('HeaderColumn.Country'),
      cell: ({ row }) => <TableCell.Text value={tc(row.original.country.name)} className="min-w-0" />
    },
    {
      accessorKey: 'sellerName',
      header: t<string>('HeaderColumn.Seller Name'),
      cell: ({ row }) => <TableCell.Text value={row.original.sellerName} className="min-w-0" />
    },
    {
      accessorKey: 'category',
      header: t<string>('HeaderColumn.Category'),
      cell: ({ row }) => <TableCell.Text value={row.original.category.name} className="min-w-0" />
    },
    {
      accessorKey: 'commissionRate',
      header: t<string>('HeaderColumn.Commission Rate'),
      meta: { align: 'right' },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.commissionRate, false)} unit="%" />
    },
    {
      accessorKey: 'price',
      header: t<string>('HeaderColumn.Price'),
      meta: { align: 'right' },
      cell: ({ row }) => (
        <TableCell.Number
          className="min-w-25"
          unit={t(row.original.price.currencyId)}
          value={formatIntNumber(row.original.price.amount)}
        />
      )
    }
  ];

  return { columns };
};
